import { useMemo } from 'react';
import { ActorLogicFrom, ActorRefFrom, fromPromise } from 'xstate';
import alloy from '@alloyidentity/web-sdk';
import { isLeft } from 'fp-ts/lib/Either';
import { NeuroIDResponse } from '@ads-bread/shared/bread/codecs';
import { createLogger } from '../../../../lib/logger';
import { useMerchantID } from '../../../../components/XPropsContext';
import { useAppConfig } from '../../../../components/AppConfigContext';
import { fraudAlloyMachine } from './fraudAlloyMachine';
import { InitializingNeuroIdResult } from './types/actors';
import { shouldUseMockProvider, mockAlloy } from './utils';
import { assignNeuroIdTokens, emitFraudAlloyMachineReady } from './assigns';

const logger = createLogger({
  name: 'checkout.fraud.alloy',
});

export type FraudAlloyMachineActorRef = ActorRefFrom<typeof fraudAlloyMachine>;

export type FraudAlloyMachineActorRefLogic = ActorLogicFrom<
  typeof fraudAlloyMachine
>;

export const useFraudAlloyMachineLogic = (): FraudAlloyMachineActorRefLogic => {
  const {
    environment,
    fraudAlloyBaseAPIKey,
    fraudAlloyBaseJourneyToken,
    tenant,
  } = useAppConfig();
  const { merchantID } = useMerchantID();

  const fraudAlloyMachineLogic = useMemo(() => {
    return fraudAlloyMachine.provide({
      actions: {
        assignNeuroIdTokens,
        emitFraudAlloyMachineReady,
        logError: (_, params) => {
          logger.error(
            { err: params.error, merchantID },
            `Error in FraudAllowMachine: ${params.type}`
          );
        },
      },
      actors: {
        initializeNeuroId: fromPromise<InitializingNeuroIdResult>(async () => {
          const alloyLib = shouldUseMockProvider() ? mockAlloy : alloy;

          if (tenant !== 'ADS') {
            return { neuroIdTokens: undefined };
          }

          const alloyInit = await alloyLib.init({
            key: fraudAlloyBaseAPIKey,
            production: environment === 'production',
            journeyToken: fraudAlloyBaseJourneyToken,
          });

          const decoded = NeuroIDResponse.decode(alloyInit);

          if (isLeft(decoded)) {
            logger.warn(
              'Unexpected response from Alloy init. Continuing without NeuroID'
            );
            throw new Error('initializeNeuroId failed!');
          }

          const neuroIdTokens: NeuroIDResponse = decoded.right;

          return { neuroIdTokens };
        }),
      },
    });
  }, []);

  return fraudAlloyMachineLogic;
};
